import React, { createContext, useContext, useEffect, useRef } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";

const WebSocketContext = createContext(null);

const PING_INTERVAL_MS = 25000;
const PONG_TIMEOUT_MS = 8000;

export const WebSocketProvider = ({ children }) => {
  const WS_ENDPOINT_URL = process.env.REACT_APP_WS_URL || "ws://localhost:3013/ws/";

  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(
    WS_ENDPOINT_URL,
    {
      // Read-only display client — no JWT, so the socket never dies on token expiry
      protocols: ["display"],
      shouldReconnect: () => true,
      reconnectAttempts: Infinity,
      reconnectInterval: (attempt) => Math.min(2000 * Math.max(attempt, 1), 15000),
      retryOnError: true,
      onOpen: () => {
        console.log("✅ WebSocket connected to", WS_ENDPOINT_URL);
      },
      onClose: (event) => {
        console.log("⚠️ WebSocket closed", event?.code, event?.reason || "");
      },
      onError: (event) => {
        console.error("❌ WebSocket error", event);
      },
    }
  );

  const pingInterval = useRef(null);
  const pongTimeout = useRef(null);

  const clearHeartbeat = () => {
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }
    if (pongTimeout.current) {
      clearTimeout(pongTimeout.current);
      pongTimeout.current = null;
    }
  };

  const forceReconnect = () => {
    const ws = getWebSocket();
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    if (ws && ws.readyState !== WebSocket.CLOSED) {
      try {
        ws.close();
      } catch {}
    }
  };

  // Keepalive ping — 25s stays under typical 60s proxy idle timeouts
  useEffect(() => {
    clearHeartbeat();
    if (readyState !== ReadyState.OPEN) {
      return clearHeartbeat;
    }

    pingInterval.current = setInterval(() => {
      try {
        sendMessage(JSON.stringify({ type: "ping" }));
      } catch (err) {
        console.error("Failed to send ping:", err);
        return;
      }

      if (pongTimeout.current) clearTimeout(pongTimeout.current);
      pongTimeout.current = setTimeout(() => {
        console.log("No pong received, forcing reconnect");
        const ws = getWebSocket();
        if (ws) {
          try {
            ws.close();
          } catch {}
        }
      }, PONG_TIMEOUT_MS);
    }, PING_INTERVAL_MS);

    return clearHeartbeat;
  }, [readyState, sendMessage, getWebSocket]);

  // Clear pong watchdog when the server replies
  useEffect(() => {
    if (!lastMessage) return;
    try {
      const data = JSON.parse(lastMessage.data);
      if (data?.type === "pong" && pongTimeout.current) {
        clearTimeout(pongTimeout.current);
        pongTimeout.current = null;
      }
    } catch {}
  }, [lastMessage]);

  // Browsers throttle timers on idle screens (welcome). Reconnect when the
  // display wakes or the network returns so admin control is never stuck.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") forceReconnect();
    };
    const onOnline = () => forceReconnect();

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, [getWebSocket]);

  return (
    <WebSocketContext.Provider value={{ sendMessage, lastMessage, readyState }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => useContext(WebSocketContext);
